import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchAcceptChallenge, {
  AcceptChallengeTokenData,
} from 'features/auth/resetPassword/utils/fetchAcceptChallenge/fetchAcceptChallenge';
import {
  MagicLinkErrorCodes,
  VerifyIdentityErrorScreenEnum,
} from 'features/otp/challenges/VerifyIdentity/VerifyIdentityError/VerifyIdentityErrorScreenEnum';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { FeatureFlagKey } from 'services/statsig/types';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { setItem } from 'shared/utils/asyncStorage';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const MagicLinkToken = () => {
  const router = useRouter();
  const { tokenRef } = useLocalSearchParams<{ tokenRef: string }>();
  const { segmentClient } = useAnalyticsConfiguration();
  const { initiateWhereToGo, isLoading: isWhereToGoLoading } = useWhereToGo();
  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();
  const magicLinkToken = tokenRef as string;
  const [isLoading, setIsLoading] = useState(false);
  const hasProcessedToken = useRef(false);
  const { getVaroSessionId, idToken, storeIdToken, handleLogout, isLoggedIn } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.ACCEPT_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  const signedInWithMagicLink = usePressableAnalytics({
    analyticsId: 'signedInWithMagicLink',
    customAttributes: {
      login_method: 'magic_link',
    },
  });

  useLayoutEffect(() => {
    const confirmToken = async () => {
      try {
        setIsLoading(true);
        if (isLoggedIn) {
          await handleLogout({
            shouldNavigateToLogin: false,
          });
        }
        const data = {
          acceptOptions: {
            tokenOption: {
              tokenReference: magicLinkToken,
            },
          },
        } as AcceptChallengeTokenData;
        const varoSessionId = await getVaroSessionId();
        const headers = await getLambdaHeaders(idToken, varoSessionId);
        const anonymousId = await segmentClient.getAnonymousId();
        const {
          challengeVerified = null,
          errorCode,
          acceptChallengeId,
        } = await fetchAcceptChallenge(
          authUrl,
          {
            ...data,
          },
          headers,
        );

        if (challengeVerified === true) {
          const verifyChallengeResult = await verifyChallenge(
            {
              challengeId: acceptChallengeId,
              anonymousId: anonymousId,
            },
            {},
            headers,
          );
          const attrData =
            verifyChallengeResult &&
            verifyChallengeResult.success === true &&
            verifyChallengeResult?.data;
          if (attrData && attrData.idToken && attrData.refreshToken) {
            await setItem(STORAGE_KEYS.REFRESH_TOKEN, attrData.refreshToken);
            await storeIdToken(attrData.idToken);
            setIsLoading(false);
            signedInWithMagicLink();
            initiateWhereToGo();
          } else {
            router.replace({
              pathname: '/login/verify-identity-error',
              params: {
                screenType: getErrorScreen(
                  verifyChallengeResult?.errorCode as MagicLinkErrorCodes,
                ),
              },
            });
          }
        } else {
          setIsLoading(false);
          if (errorCode) {
            router.replace({
              pathname: '/login/verify-identity-error',
              params: {
                screenType: getErrorScreen(errorCode),
              },
            });
          }
        }
      } catch (error) {
        setIsLoading(false);
        logErrorWithContext(
          'AcceptChallenge error in magic link token: ' + error,
          {
            featureArea: FeatureArea.AUTHENTICATION,
            responsibleTeam: ResponsibleTeam.FRAUD,
            severity: ErrorSeverity.CRITICAL,
          },
        );
      }
    };

    if (magicLinkToken && !hasProcessedToken.current) {
      hasProcessedToken.current = true;
      void confirmToken();
    }
  }, [
    authUrl,
    getVaroSessionId,
    handleLogout,
    idToken,
    initiateWhereToGo,
    isLoggedIn,
    magicLinkToken,
    router,
    segmentClient,
    signedInWithMagicLink,
    storeIdToken,
    verifyChallenge,
  ]);

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={isLoading || isVerifyChallengeLoading || isWhereToGoLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};

export const getErrorScreen = (errorCode: MagicLinkErrorCodes) => {
  switch (errorCode) {
    case MagicLinkErrorCodes.LinkExpired:
      return VerifyIdentityErrorScreenEnum.LinkExpired;
    case MagicLinkErrorCodes.TooManyRequests:
      return VerifyIdentityErrorScreenEnum.TooManyRequests;
    default:
      return VerifyIdentityErrorScreenEnum.Error;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default MagicLinkToken;
